import React, { useMemo } from 'react';
import { Box, ListItem, ListItemAvatar, ListItemText, Typography, Badge } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { format } from 'date-fns';
import type { IRoom } from '../../redux/messages/types';
import { createFullName, getJWtDetails } from '../../utils/AppUtils';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MicIcon from '@mui/icons-material/Mic';
import ImageIcon from '@mui/icons-material/Image';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideocamIcon from '@mui/icons-material/Videocam';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

interface IRoomItemProps {
    room: IRoom;
    onClick: (roomId: string) => void;
    isActive?: boolean;
    showDivider?: boolean;
    /** Floating messages panel: casting-style row (hover + active accent). */
    visualVariant?: 'default' | 'floatingPanel';
}

const ZINC_800_50 = 'rgba(39, 39, 42, 0.5)';

const RoomItem: React.FC<IRoomItemProps> = ({ room, onClick, isActive, showDivider, visualVariant = 'default' }) => {
    const { themeStyle } = useThemeContext();
    const isFloatingPanel = visualVariant === 'floatingPanel';
    const listRowHoverBgFloating = themeStyle === 'light' ? 'rgba(0, 0, 0, 0.055)' : 'rgba(255, 255, 255, 0.07)';
    const mainListActiveBg =
        themeStyle === 'light' ? alpha(theme[themeStyle].true_black, 0.06) : alpha(theme[themeStyle].tag_white, 0.06);
    const mainListHoverBg =
        themeStyle === 'light' ? alpha(theme[themeStyle].true_black, 0.04) : alpha(theme[themeStyle].tag_white, 0.065);
    const mainListHoverActiveBg =
        themeStyle === 'light' ? alpha(theme[themeStyle].true_black, 0.08) : alpha(theme[themeStyle].tag_white, 0.09);
    const { user_id } = getJWtDetails();
    const isSender = room.sender && room.sender._id === user_id;

    const othersTyping =
        Array.isArray(room.userTyping) && room.userTyping.some(id => id != null && String(id) !== String(user_id));
    const isTyping = room.isUserTyping === 'yes' || othersTyping;
    const roomName = useMemo(() => {
        if (isSender && room.receiver?.length) {
            const recipients = room.receiver.filter(user => typeof user !== 'string' && user._id !== user_id);
            if (recipients.length === 0) return 'No recipient';
            if (recipients.length === 1) return createFullName(recipients[0]);
            return `${createFullName(recipients[0])} +${recipients.length - 1}`;
        } else if (room.sender && typeof room.sender !== 'string') {
            const others = room.receiver.filter(user => typeof user !== 'string' && user._id !== user_id);
            if (others.length > 0) return `${createFullName(room.sender)} +${others.length}`;
            return createFullName(room.sender);
        }
        return 'Conversation';
    }, [room, user_id, isSender]);

    const roomAvatars = useMemo(() => {
        if (isSender && room.receiver?.length) {
            return room.receiver.filter(user => typeof user !== 'string' && user._id !== user_id).slice(0, 3);
        } else if (room.sender && typeof room.sender !== 'string') {
            const otherReceivers = room.receiver
                .filter(user => typeof user !== 'string' && user._id !== user_id)
                .slice(0, 2);
            return [room.sender, ...otherReceivers];
        }
        return [];
    }, [room, user_id, isSender]);

    const totalParticipantsCount = useMemo(() => {
        let count = 0;
        if (room.receiver) {
            count += room.receiver.filter(user =>
                typeof user === 'string' ? user !== user_id : user._id !== user_id,
            ).length;
        }
        if (room.sender && typeof room.sender !== 'string' && room.sender._id !== user_id) {
            count += 1;
        }
        return count;
    }, [room, user_id]);

    const unreadCount = useMemo(() => {
        const latestMessage = room.messages?.[0];
        const latestMessageAuthorId =
            typeof latestMessage?.created_by === 'string' ? latestMessage.created_by : latestMessage?.created_by?._id;
        const isLatestMessageSentByCurrentUser =
            latestMessageAuthorId != null && String(latestMessageAuthorId) === String(user_id);
        if (isLatestMessageSentByCurrentUser) {
            return 0;
        }

        if (room.unread_message_count?.length) {
            return room.unread_message_count.find(c => String(c.user) === String(user_id))?.count || 0;
        }
        return 0;
    }, [room.messages, room.unread_message_count, user_id]);

    const lastMessagePreview = useMemo(() => {
        const lastMessage = room.messages?.[0];

        if (!lastMessage) return '';

        if (isTyping) return 'Typing...';

        if (lastMessage.message_content) return lastMessage.message_content;

        const messageType = lastMessage.message_type || 'chat';

        switch (messageType) {
            case 'document_message':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <InsertDriveFileIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: theme[themeStyle].highlight, fontSize: '14px' }}
                        />
                        <span>Document</span>
                    </Box>
                );
            case 'sound_message':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MicIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: theme[themeStyle].highlight, fontSize: '14px' }}
                        />
                        <span>Audio</span>
                    </Box>
                );
            case 'picture_message':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ImageIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: theme[themeStyle].highlight, fontSize: '14px' }}
                        />
                        <span>Photo</span>
                    </Box>
                );
            case 'location_message':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOnIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: theme[themeStyle].highlight, fontSize: '14px' }}
                        />
                        <span>Location</span>
                    </Box>
                );
            case 'video_message':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <VideocamIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: theme[themeStyle].highlight, fontSize: '14px' }}
                        />
                        <span>Video</span>
                    </Box>
                );
            case 'audition_invite':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EventAvailableIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: theme[themeStyle].highlight, fontSize: '14px' }}
                        />
                        <span>Audition Invitation</span>
                    </Box>
                );
            default:
                return lastMessage.message_content || '';
        }
    }, [room.messages, isTyping, themeStyle]);

    const lastMessageTime = useMemo(() => {
        try {
            const date = new Date(room.last_message_at);
            const today = new Date();
            const isToday = date.toDateString() === today.toDateString();
            return format(date, isToday ? 'h:mm a' : 'MM/dd/yy');
        } catch {
            return '';
        }
    }, [room.last_message_at]);

    const avatarSize = isFloatingPanel ? 48 : 48;
    const avatarOverlap = isFloatingPanel ? 14 : 16;
    return (
        <>
            <ListItem
                onClick={() => onClick(room._id)}
                sx={{
                    py: isFloatingPanel ? 2.1 : 2,
                    px: isFloatingPanel ? 2.5 : 2,
                    gap: isFloatingPanel ? 0 : 2,
                    cursor: 'pointer',
                    alignItems: isFloatingPanel ? 'flex-start' : 'center',
                    borderRadius: isFloatingPanel ? 0 : 0,
                    transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    ...(isFloatingPanel
                        ? {
                              backgroundColor: isActive ? ZINC_800_50 : theme[themeStyle].deep_grey,
                              borderLeft: isActive
                                  ? `2px solid ${theme[themeStyle].highlight}`
                                  : '2px solid transparent',
                              '@media (hover: hover)': {
                                  '&:hover': {
                                      backgroundColor: isActive ? 'rgba(255, 255, 255, 0.09)' : listRowHoverBgFloating,
                                  },
                              },
                          }
                        : {
                              backgroundColor: isActive ? mainListActiveBg : 'transparent',
                              borderLeft: isActive
                                  ? `2px solid ${theme[themeStyle].highlight}`
                                  : '2px solid transparent',
                              '@media (hover: hover)': {
                                  '&:hover': {
                                      backgroundColor: isActive ? mainListHoverActiveBg : mainListHoverBg,
                                  },
                              },
                          }),
                }}>
                <ListItemAvatar>
                    <Box
                        sx={{
                            position: 'relative',
                            height: avatarSize,
                            width:
                                roomAvatars.length > 1
                                    ? avatarSize + (roomAvatars.length - 1) * avatarOverlap
                                    : avatarSize,
                        }}>
                        {roomAvatars.map((user, index) => (
                            <Box
                                key={user._id || index}
                                sx={{
                                    position: 'absolute',
                                    left: index * avatarOverlap,
                                    zIndex: 3 - index,
                                    border: isFloatingPanel
                                        ? '1px solid rgba(255,255,255,0.12)'
                                        : `1px solid ${theme[themeStyle].secondaryBg}`,
                                    borderRadius: '50%',
                                }}>
                                <ThspianAvatar
                                    user={user}
                                    sx={{
                                        width: avatarSize,
                                        height: avatarSize,
                                    }}
                                    withMonogram
                                />
                            </Box>
                        ))}

                        {totalParticipantsCount > 3 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: -5,
                                    bottom: -3,
                                    backgroundColor: theme[themeStyle].highlight,
                                    color: theme[themeStyle].true_white,
                                    borderRadius: '50%',
                                    width: 22,
                                    height: 22,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 11,
                                    fontWeight: 'bold',
                                    border: `1px solid ${theme[themeStyle].secondaryBg}`,
                                    zIndex: 4,
                                }}>
                                +{totalParticipantsCount - Math.min(3, roomAvatars.length)}
                            </Box>
                        )}
                    </Box>
                </ListItemAvatar>
                <ListItemText
                    disableTypography
                    primary={
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: isFloatingPanel ? 'baseline' : 'center',
                                gap: isFloatingPanel ? 1 : 0,
                                minWidth: 0,
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: theme[themeStyle].tag_white,
                                    fontSize: isFloatingPanel ? '0.9375rem' : '0.875rem',
                                    fontWeight: isFloatingPanel ? 600 : 700,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: isFloatingPanel ? undefined : undefined,
                                    flex: isFloatingPanel ? 1 : 1,
                                    minWidth: 0,
                                    display: isFloatingPanel ? 'block' : 'block',
                                    textTransform: 'capitalize',
                                }}>
                                {roomName}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: isFloatingPanel
                                        ? 'rgba(255,255,255,0.45)'
                                        : isActive
                                        ? theme[themeStyle].highlight
                                        : theme[themeStyle].grey,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: isFloatingPanel ? '0.75rem' : '10px',
                                    flexShrink: 0,
                                }}>
                                {lastMessageTime}
                            </Typography>
                        </Box>
                    }
                    secondary={
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 0.5,
                                minWidth: 0,
                            }}>
                            <Typography
                                sx={{
                                    color: isFloatingPanel
                                        ? 'rgba(255,255,255,0.5)'
                                        : isActive
                                        ? theme[themeStyle].tag_white
                                        : theme[themeStyle].grey,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: isFloatingPanel ? '0.875rem' : '0.75rem',
                                    fontWeight: isActive && !isFloatingPanel ? 500 : 400,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    maxWidth: isFloatingPanel ? '100%' : '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    opacity: isActive && !isFloatingPanel ? 0.95 : isFloatingPanel ? 1 : 0.92,
                                }}>
                                {lastMessagePreview}
                            </Typography>
                            {unreadCount > 0 && (
                                <Badge
                                    badgeContent={unreadCount}
                                    color="error"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            backgroundColor: theme[themeStyle].primary_red,
                                            color: theme[themeStyle].true_white,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 10,
                                        },
                                        position: 'absolute',
                                        left: 20,
                                        top: 30,
                                        zIndex: 9999,
                                    }}
                                />
                            )}
                        </Box>
                    }
                />
            </ListItem>
        </>
    );
};

export default RoomItem;
